import { useState, useCallback, useEffect } from 'react'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay } from 'date-fns'
import type { TimeSlot, WeekDay, ArtistAvailability } from '@/types/booking'

interface UseBookingCalendarReturn {
  currentMonth: Date
  selectedDate: Date | null
  selectedTimeSlot: { start: number; end: number } | null
  slideDirection: number
  justSelectedDate: boolean
  handlePrevMonth: () => void
  handleNextMonth: () => void
  handleDayClick: (date: Date) => void
  handleTimeSlotClick: (slot: { start: number; end: number }) => void
  getAvailableTimeSlots: (date: Date) => TimeSlot[]
  get30MinuteIntervals: (timeSlots: TimeSlot[]) => { start: number; end: number }[]
  hasAvailableSlots: (date: Date) => boolean
  formatTime: (minutes: number) => string
  daysInMonth: Date[]
  paddingDays: (null)[]
  dayNames: string[]
  monthStart: Date
  monthEnd: Date
}

/**
 * Helper to convert day index to WeekDay
 */
const getDayName = (date: Date): WeekDay => {
  const days: WeekDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  return days[getDay(date)]
}

/**
 * Helper to format time from minutes
 */
export const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

/**
 * Subtract blocked time slots from available time slots
 * Example: Available 9am-5pm (540-1020), Blocked 2pm-3pm (840-900)
 * Result: [9am-2pm (540-840), 3pm-5pm (900-1020)]
 */
const subtractBlockedSlots = (available: TimeSlot[], blocked: TimeSlot[]): TimeSlot[] => {
  if (blocked.length === 0) return available

  const result: TimeSlot[] = []

  available.forEach(availSlot => {
    let currentStart = availSlot.start

    // Sort blocked slots by start time
    const sortedBlocked = [...blocked].sort((a, b) => a.start - b.start)

    sortedBlocked.forEach(blockedSlot => {
      // If the blocked slot doesn't overlap with remaining availability, skip it
      if (blockedSlot.end <= currentStart || blockedSlot.start >= availSlot.end) {
        return
      }

      // Add the gap before the blocked slot (if any)
      if (currentStart < blockedSlot.start) {
        result.push({
          start: currentStart,
          end: blockedSlot.start
        })
      }

      // Move current position past the blocked slot
      currentStart = Math.max(currentStart, blockedSlot.end)
    })

    // Add any remaining availability after processing blocked slots
    if (currentStart < availSlot.end) {
      result.push({
        start: currentStart,
        end: availSlot.end
      })
    }
  })

  return result
}

export function useBookingCalendar(
  availability: ArtistAvailability | null,
  earliestDate: Date,
  estimatedTime: number | 'unknown'
): UseBookingCalendarReturn {
  const [currentMonth, setCurrentMonth] = useState(earliestDate || new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ start: number; end: number } | null>(null)
  const [slideDirection, setSlideDirection] = useState(0)
  const [justSelectedDate, setJustSelectedDate] = useState(false)

  const handlePrevMonth = useCallback(() => {
    setSlideDirection(-1)
    setCurrentMonth(prev => subMonths(prev, 1))
  }, [])

  const handleNextMonth = useCallback(() => {
    setSlideDirection(1)
    setCurrentMonth(prev => addMonths(prev, 1))
  }, [])

  const handleDayClick = useCallback((date: Date) => {
    const isSelecting = !isSameDay(date, selectedDate || new Date('1970-01-01'))
    setSelectedDate(isSelecting ? date : null)
    setSelectedTimeSlot(null) // Reset time slot when changing date
    if (isSelecting) {
      setJustSelectedDate(true) // Trigger the highlight animation
    }
  }, [selectedDate])

  // Reset justSelectedDate flag after animation completes
  useEffect(() => {
    if (justSelectedDate) {
      const timer = setTimeout(() => {
        setJustSelectedDate(false)
      }, 300) // Match the animation duration
      return () => clearTimeout(timer)
    }
  }, [justSelectedDate])

  const handleTimeSlotClick = useCallback((slot: { start: number; end: number }) => {
    setSelectedTimeSlot(
      selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end
        ? null
        : slot
    )
  }, [selectedTimeSlot])

  /**
   * Get available time slots for a specific date
   */
  const getAvailableTimeSlots = useCallback((date: Date): TimeSlot[] => {
    if (!availability) return []

    const dateString = format(date, 'yyyy-MM-dd')

    // Check for date override first (highest priority) - only in week mode
    if (availability.mode === 'week') {
      const dateOverride = availability.dateOverrides?.[dateString]
      if (dateOverride) {
        let slots = dateOverride.isEnabled ? dateOverride.timeSlots : []

        // Subtract Google Calendar blocked slots from date override
        const googleOverride = availability.googleOverrides?.[dateString]
        if (googleOverride && googleOverride.isEnabled) {
          slots = subtractBlockedSlots(slots, googleOverride.timeSlots)
        }

        // Subtract existing booking blocked slots from date override
        const bookingOverride = availability.bookingOverrides?.[dateString]
        if (bookingOverride && bookingOverride.isEnabled) {
          slots = subtractBlockedSlots(slots, bookingOverride.timeSlots)
        }

        return slots
      }
    }

    // Determine which schedule mode to use
    const isMonthMode = availability.mode === 'month'

    // In month mode: ONLY use monthSchedule, no fallback to weekSchedule
    if (isMonthMode) {
      const monthScheduleEntry = availability.monthSchedule?.[dateString]
      if (!monthScheduleEntry) {
        return [] // No availability if date not in monthSchedule
      }

      let slots = monthScheduleEntry.isEnabled
        ? monthScheduleEntry.timeSlots
        : []

      // Subtract Google Calendar blocked slots from month schedule
      const googleOverride = availability.googleOverrides?.[dateString]
      if (googleOverride && googleOverride.isEnabled) {
        slots = subtractBlockedSlots(slots, googleOverride.timeSlots)
      }

      // Subtract existing booking blocked slots from month schedule
      const bookingOverride = availability.bookingOverrides?.[dateString]
      if (bookingOverride && bookingOverride.isEnabled) {
        slots = subtractBlockedSlots(slots, bookingOverride.timeSlots)
      }

      return slots
    }

    // In week mode: use weekSchedule (or weeklySchedule for backward compatibility)
    const dayName = getDayName(date)
    const weekSchedule = availability.weekSchedule || (availability as any).weeklySchedule

    if (!weekSchedule) return []

    const daySettings = weekSchedule[dayName]

    if (!daySettings?.isEnabled) return []

    // Subtract Google Calendar blocked slots from weekly schedule
    let availableSlots = daySettings.timeSlots
    const googleOverride = availability.googleOverrides?.[dateString]
    if (googleOverride && googleOverride.isEnabled) {
      availableSlots = subtractBlockedSlots(availableSlots, googleOverride.timeSlots)
    }

    // Subtract existing booking blocked slots from weekly schedule
    const bookingOverride = availability.bookingOverrides?.[dateString]
    if (bookingOverride && bookingOverride.isEnabled) {
      availableSlots = subtractBlockedSlots(availableSlots, bookingOverride.timeSlots)
    }

    return availableSlots
  }, [availability])

  /**
   * Break time slots into 30-minute intervals
   */
  const get30MinuteIntervals = useCallback((timeSlots: TimeSlot[]): { start: number; end: number }[] => {
    const intervals: { start: number; end: number }[] = []

    // Convert estimated time to minutes (handle 'unknown' case - default to 1 hour)
    const durationMinutes = estimatedTime === 'unknown' ? 60 : estimatedTime * 60

    timeSlots.forEach(slot => {
      let currentStart = slot.start
      while (currentStart + 30 <= slot.end) {
        // Check if there's enough time to complete the tattoo after this slot starts
        const remainingTime = slot.end - currentStart

        // Only add the slot if there's enough time for the estimated duration
        // If duration is unknown, include all slots
        if (durationMinutes === 0 || remainingTime >= durationMinutes) {
          intervals.push({
            start: currentStart,
            end: currentStart + 30
          })
        }
        currentStart += 30
      }
    })

    return intervals
  }, [estimatedTime])

  /**
   * Check if a date has any available time slots
   */
  const hasAvailableSlots = useCallback((date: Date): boolean => {
    const slots = getAvailableTimeSlots(date)
    return get30MinuteIntervals(slots).length > 0
  }, [getAvailableTimeSlots, get30MinuteIntervals])

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Get day names for the header (Mon-Sun)
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  // Calculate padding days for the start of the month
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  return {
    currentMonth,
    selectedDate,
    selectedTimeSlot,
    slideDirection,
    justSelectedDate,
    handlePrevMonth,
    handleNextMonth,
    handleDayClick,
    handleTimeSlotClick,
    getAvailableTimeSlots,
    get30MinuteIntervals,
    hasAvailableSlots,
    formatTime,
    daysInMonth,
    paddingDays,
    dayNames,
    monthStart,
    monthEnd
  }
}
